<template>
 <div v-if="canInteract" class="interact-buttons">
    <button class="button is-primary" @click="toggleConnection">{{label}} <span v-if="clickedConnect" class="loader gtm-profile-connect"></span></button>
    <button v-if="!$isYSC && !$isIsef" class="button is-primary" @click="messageUser">{{$t('interact.message')}}</button>
    <button class="button is-primary" v-if="isAdmin" @click="viewAdminDashboard"><span class="icon gtm-profile-message"><i class="fal fa-lock"></i></span>{{$t('interact.edit')}}<span>&nbsp;/ {{$t('interact.details')}}</span></button>
    <button class="button is-primary" v-if="isAdmin && !$isApp" @click="becomeUser"><span class="icon"><i class="fal fa-user-secret"></i></span>&nbsp;&nbsp;{{$t('interact.become')}}</button>
    <button class="button is-primary" v-if="isAdmin && !$isApp" @click="reportUserEmail"><span class="icon"><i class="fal fa-exclamation-square"></i></span>&nbsp;&nbsp;{{$t('interact.spam')}}</button>
    <modal-custom v-if="showDisconnectModal" :title="'Unfollow'" :text="'Are you sure you want to unfollow this user?'"
    :hasFooter="true" :successText="'Unfollow'" :cancelText="'Cancel'"
    @modalSuccess="confirmDisconnect" @modalCancel="showDisconnectModal = false"></modal-custom>
    <fade-message :message="message" :error="error" @clearMessage="message = ''" @clearError="error = ''"></fade-message>
  </div>
</template>

<script>
import throttle from 'lodash/throttle'
import auth from '@/mixins/auth'

import ModalCustom from '@/components/Modals/Custom'
import FadeMessage from '@/components/Controls/FadeMessage'
import i18n from '@/i18n'
import admin from '@/api/admin'
import spam from '@/api/spam'

export default {
  components: {
    ModalCustom,
    FadeMessage
  },

  mixins: [auth],

  props: ['user'],

  data: function () {
    return {
      followingUser: false,
      clickedConnect: false,
      checkedConnection: null,
      showDisconnectModal: false,
      message: '',
      error: ''
    }
  },

  computed: {
    label: function () {
      return !this.followingUser ? i18n.t('interact.follow') : i18n.t('interact.unfollow')
    },
    canInteract: function () {
      if (!this.hasUser) {
        return false
      }

      if (!this.isLoggedIn) {
        return true
      }

      return this.currentUser.uuid !== this.user.uuid
    },
    hasUser: function () {
      return this.user && this.user.userName
    }
  },

  mounted: function () {
    this.checkConnection()
  },

  watch: {
    user: function () {
      if (this.hasUser && this.checkedConnection !== this.user.uuid) {
        this.checkConnection()
      }
    }
  },

  methods: {
    checkConnection: function () {
      if (this.isLoggedIn && this.hasUser) {
        this.checkedConnection = this.user.uuid
        this.followingUser = false
        this.$store.dispatch('checkForUserConnection', { username: this.currentUser.userName, otherUsername: this.user.userName }).then((following) => {
          this.followingUser = following
        }, (error) => {
          this.error = error.message
        })
      }
    },
    toggleConnection: function () {
      if (this.followingUser) {
        return this.disconnect()
      }

      this.connect()
    },
    connect: throttle(function () {
      if (!this.isLoggedIn) {
        return this.$store.dispatch('showLoginModal', { source: 'CONNECT:{PATH}', onSuccess: this.toggleConnection, signupMessage: 'Sign up to connect with ' + this.user.userName })
      }

      this.clickedConnect = true
      this.error = ''

      this.$store.dispatch('connectToUser', { username: this.currentUser.userName, otherUsername: this.user.userName }).then((response) => {
        this.clickedConnect = false
        this.message = `You are now following ${this.user.userName}`
        this.followingUser = true
        this.$emit('getConnections')
      }, (error) => {
        this.error = error.message
      })
    }, 500, { leading: true, trailing: false }),
    disconnect: function () {
      this.showDisconnectModal = true
    },
    confirmDisconnect: throttle(function () {
      this.showDisconnectModal = false
      this.clickedConnect = true
      this.error = ''
      this.$store.dispatch('disconnectFromUser', { username: this.currentUser.userName, otherUsername: this.user.userName }).then((response) => {
        this.clickedConnect = false
        this.message = `You have unfollowed ${this.user.userName}`
        this.followingUser = false
        this.$emit('getConnections')
      }, (error) => {
        this.clickedConnect = false
        this.error = error.message
      })
    }, 500, { leading: true, trailing: false }),
    messageUser: function () {
      if (!this.isLoggedIn) {
        return this.$store.dispatch('showLoginModal', { source: 'MESSAGE:{PATH}', onSuccess: this.messageUser, signupMessage: 'Sign up to send ' + this.user.userName + ' a message' })
      }
      this.$router.push({ name: 'messaging', params: { username: this.user.userName } })
    },
    viewAdminDashboard: function () {
      this.$router.push({ name: 'admin', params: { username: this.user.userName, component: 'UsersEdit' } })
    },
    becomeUser: function () {
      this.$router.push({ name: 'admin', params: { username: this.user.userName, redirect: this.getProfileURL(), component: 'UsersBecome' } })
    },
    reportUserEmail: function () {
      spam.addUserRule(this.user.userName).then(() => {
        admin.softDeleteUser(this.user.userName).then((response) => {
          this.message = 'User email added to spam rules and will not show in user searches, you can remove the rule from the admin page.'
        }, () => {
          this.error = 'Failed to soft delete user'
        })
      }, () => {
        this.error = 'Failed to add user email added to spam rules'
      })
    },
    getProfileURL: function () {
      const base = process.env.BASE
      if (base === undefined || base === '/') {
        return '/profile'
      } else {
        return `${base}/profile`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.interact-buttons {
  text-align: center;
  margin-top: 1rem;
}

button {
  margin-bottom: 0.5rem;
  border-radius: 0;
}

</style>
